import { BarChartOutlined, ExperimentOutlined, FullscreenOutlined, QuestionCircleTwoTone, TableOutlined } from "@ant-design/icons";
import { Button, Modal, Spin, Typography } from "antd";
import { showError, showSuccess, showWarning } from "common/ToastNotifications";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import Tooltip from "components/atoms/Tooltip";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import AIButton from "components/molecules/AIButton";
import DatasetDescriptionModal from "components/molecules/DatasetDescriptionModal";
import DataSetInsightsList from "components/organisms/DataSetInsightsList";
import ProfilingStatistics from "components/organisms/ProfilingStatistics";
import { buildDistributions } from "components/organisms/ProfilingStatistics/distributionUtils";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";

const { Title } = Typography;

function extractProfilingData(fieldInsights) {
    if (!fieldInsights || fieldInsights.length === 0) return null;

    // Build fieldId -> field metadata lookup from quality records
    const fieldNameMap = {};
    const fieldDescriptionMap = {};
    fieldInsights.forEach((f) => {
        if (f.fieldName && f.fieldId) {
            fieldNameMap[f.fieldId] = f.fieldName;
        }
        if (f.fieldDescription && f.fieldId) {
            fieldDescriptionMap[f.fieldId] = f.fieldDescription;
        }
    });

    const columnsWithProfiling = fieldInsights.filter(
        (f) => f.profilingStats && Object.keys(f.profilingStats).length > 0
    );

    if (columnsWithProfiling.length === 0) return null;

    let totalRecords = null;
    let lastUpdated = null;

    const columns = columnsWithProfiling.map((field) => {
        const stats = field.profilingStats;
        if (stats.totalRecords != null) totalRecords = stats.totalRecords;
        if (stats.profiledAt && (!lastUpdated || stats.profiledAt > lastUpdated)) {
            lastUpdated = stats.profiledAt;
        }

        // Resolve field name and description: try field directly, then lookup by fieldId
        const fieldName = field.fieldName || fieldNameMap[field.fieldId] || field.fieldId;
        const fieldDescription = field.fieldDescription || fieldDescriptionMap[field.fieldId];

        // Map API field names to UI field names
        const nullPercent = stats.nullPercent != null ? stats.nullPercent : stats.nullPct;
        const blankPercent = stats.blankPercent != null ? stats.blankPercent : stats.blankPct;
        const completePercent = stats.completePercent != null ? stats.completePercent : stats.completePct;
        const distinctPercent = stats.distinctPercent != null ? stats.distinctPercent
            : (stats.uniquenessPct != null ? stats.uniquenessPct : null);

        const distributionData = buildDistributions(stats, {
            compactEntries: stats.valueDistribution || stats.topValues,
            expandedEntries: stats.topValuesExpanded,
        });

        // Extract numeric stats for range
        const numStats = stats.numericStats;
        const strStats = stats.stringStats;
        const rangeMin = stats.rangeMin != null ? stats.rangeMin : (numStats ? numStats.min : null);
        const rangeMax = stats.rangeMax != null ? stats.rangeMax : (numStats ? numStats.max : null);
        const lengthMin = stats.lengthMin != null ? stats.lengthMin : (strStats ? strStats.minLength : null);
        const lengthMax = stats.lengthMax != null ? stats.lengthMax : (strStats ? strStats.maxLength : null);
        const lengthAvg = stats.lengthAvg != null ? stats.lengthAvg : (strStats ? strStats.avgLength : null);
        const approximateMedian = stats.approximateMedian != null ? stats.approximateMedian : (numStats ? numStats.median : null);

        // Extract most popular from topValues if not directly available
        let mostPopularValue = stats.mostPopularValue;
        let mostPopularFrequency = stats.mostPopularFrequency;
        if (!mostPopularValue && distributionData.valueDistribution && distributionData.valueDistribution.length > 0) {
            const top = distributionData.valueDistribution.find((entry) => entry.bucketType === "value") || distributionData.valueDistribution[0];
            mostPopularValue = top.value;
            mostPopularFrequency = top.count;
        }

        return {
            fieldName,
            fieldDescription,
            fieldId: field.fieldId,
            sqlType: stats.sqlType || field.fieldDataType,
            inferredType: stats.inferredType && typeof stats.inferredType === "object" ? stats.inferredType.type : stats.inferredType,
            distinctCount: stats.distinctCount,
            distinctPercent,
            distinctCapped: stats.distinctCapped != null ? stats.distinctCapped : stats.distinctCountCapped,
            nullCount: stats.nullCount,
            nullPercent,
            blankCount: stats.blankCount,
            blankPercent,
            completePercent,
            mostPopularValue,
            mostPopularFrequency,
            rangeMin,
            rangeMax,
            lengthMin,
            lengthMax,
            lengthAvg,
            approximateMedian,
            skipped: stats.skipped,
            totalRecords: stats.totalRecords,
            valueDistribution: distributionData.valueDistribution,
            expandedValueDistribution: distributionData.expandedValueDistribution,
            hasTruncatedDistribution: distributionData.hasTruncatedDistribution,
            canViewAll: distributionData.canViewAll,
            distributionApproximate: distributionData.distributionApproximate,
            formatPatterns: stats.formatPatterns,
        };
    });

    return {
        totalRecords,
        totalColumns: columns.length,
        lastUpdated: lastUpdated ? lastUpdated.split("T")[0] : null,
        columns,
    };
}

class DataSetInsightsHomeOverview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fetchingDataSetInsights: false,
            busy: false,
            busyMessage: "Please wait...",
            refreshingDataSetInsights: false,
            activeTab: "quality",
            viewMode: "chart",
            profilingExpanded: false,
            aiSummaryModalOpen: false,
        };
    }

    handleOpenAiSummary = () => {
        this.setState({ aiSummaryModalOpen: true });
        const desc = this.props.datasetDescription;
        const hasSummary = desc && desc.dataSetId === this.props.dataSetId && (desc.reportHtml || desc.description);
        if (!hasSummary && !this.props.datasetDescriptionLoading) {
            this.handleGenerateDescription(false);
        }
    };

    componentWillMount() {
        if (this.props.dataSetId) {
            this.setState({
                fetchingDataSetInsights: true,
            });
            this.props.getDataSetFieldInsights(this.props.dataSetId);
            this.props.getDataSetJobs(this.props.dataSetId);
        }
    }

    componentDidMount() {}

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.onDataSetIdChanged(prevProps);
        this.onGetDataSetFieldInsightsResultChanged(prevProps);
        this.onRunProfilingResultChanged(prevProps);
    }

    componentWillUnmount() {}

    onDataSetIdChanged(prevProps) {
        if (prevProps.dataSetId !== this.props.dataSetId) {
            this.setState({
                fetchingDataSetInsights: true,
            });
            this.props.resetDatasetDescription();
            this.props.getDataSetFieldInsights(this.props.dataSetId);
            this.props.getDataSetJobs(this.props.dataSetId);
        } else if (this.props.dataSetId && prevProps.refreshFlag !== this.props.refreshFlag) {
            this.setState({
                refreshingDataSetInsights: true,
            });
            this.props.getDataSetFieldInsights(this.props.dataSetId);
        }
    }

    onGetDataSetFieldInsightsResultChanged = (prevProps) => {
        if (this.props.getDataSetFieldInsightsResult && this.props.getDataSetFieldInsightsResult !== prevProps.getDataSetFieldInsightsResult) {
            if (!this.props.getDataSetFieldInsightsResult.success) {
                if (this.props.getDataSetFieldInsightsResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true,
                    });
                } else {
                    showError("Unable to retrieve dataset insights at this moment.");
                }
            }
            this.setState({
                fetchingDataSetInsights: false,
                refreshingDataSetInsights: false,
            });
        }
    };

    onRunProfilingResultChanged = (prevProps) => {
        if (this.props.runDataSetProfilingResult && this.props.runDataSetProfilingResult !== prevProps.runDataSetProfilingResult) {
            if (!this.props.runDataSetProfilingResult.success) {
                if (this.props.runDataSetProfilingResult.code === "JOB_ALREADY_RUNNING") {
                    showWarning("A profiling job is already in progress for this dataset.");
                } else {
                    showError("Unable to start profiling job.");
                }
            } else if (prevProps.profilingJobRunning && !this.props.profilingJobRunning) {
                showSuccess("Profiling job submitted. Progress will appear in the jobs panel.");
                this.setState({ refreshingDataSetInsights: true });
                this.props.getDataSetFieldInsights(this.props.dataSetId);
                this.props.getDataSetJobs(this.props.dataSetId);
            }
        }
    };

    handleRunProfiling = () => {
        if (this.props.dataSetId) {
            this.props.runDataSetProfiling(this.props.dataSetId);
        }
    };

    // Resolve the businessAreaId/schemaId needed for the AI describe endpoint —
    // prefer the loaded dataset, fall back to the field-insights records.
    resolveDatasetContext = () => {
        const ds = this.props.dataSet;
        if (ds && ds.dataSetId === this.props.dataSetId && ds.businessAreaId && ds.schemaId) {
            return { businessAreaId: ds.businessAreaId, schemaId: ds.schemaId };
        }
        const insights = this.props.dataSetFieldInsightsResult?.Items || [];
        const match = insights.find((item) => item && item.businessAreaId && item.schemaId);
        if (match) {
            return { businessAreaId: match.businessAreaId, schemaId: match.schemaId };
        }
        return { businessAreaId: null, schemaId: null };
    };

    handleGenerateDescription = (regenerate = false) => {
        const { businessAreaId, schemaId } = this.resolveDatasetContext();
        if (businessAreaId && schemaId && this.props.dataSetId) {
            this.props.getDatasetDescription(businessAreaId, schemaId, this.props.dataSetId, regenerate);
        }
    };

    isProfilingJobActive = () => {
        if (this.props.profilingJobRunning) return true;
        const jobs = this.props.jobsResult?.Items || [];
        return jobs.some(
            (job) =>
                job.jobType === "PROFILING" &&
                (job.status === "RUNNING" || job.status === "PENDING" || job.status === "EXEC")
        );
    };

    isBusy = () => {
        return this.state.busy || this.state.fetchingDataSetInsights;
    };

    render() {
        const profilingData = extractProfilingData(this.props.dataSetFieldInsightsResult.Items);
        const profilingJobActive = this.isProfilingJobActive();
        const profilingAvailable = Boolean(profilingData && profilingData.columns && profilingData.columns.length > 0);
        const datasetContext = this.resolveDatasetContext();
        const canGenerateDescription = Boolean(
            profilingAvailable && this.props.dataSetId && datasetContext.businessAreaId && datasetContext.schemaId,
        );
        const aiSummaryForThisDataset =
            this.props.datasetDescription && this.props.datasetDescription.dataSetId === this.props.dataSetId
                ? this.props.datasetDescription
                : null;

        return (
            <FlexContainerLayout
                header={
                    <>
                        <Title level={4} className="!mb-0">
                            Dataset Insights
                        </Title>
                        {this.state.refreshingDataSetInsights ? <Spin size="small" /> : null}
                        {this.props.dataSetId ? (
                            <Tooltip title={`DataSet ID: ${this.props.dataSetId}`}>
                                <QuestionCircleTwoTone size="small" />
                            </Tooltip>
                        ) : null}
                        <div style={{ display: "flex", gap: 2, background: "#f5f5f5", borderRadius: 4, padding: 2, marginLeft: 8 }}>
                            <Button
                                type="text"
                                size="small"
                                onClick={() => this.setState({ activeTab: "quality" })}
                                style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: this.state.activeTab === "quality" ? "#0078d4" : "#666",
                                    background: this.state.activeTab === "quality" ? "#fff" : "transparent",
                                    boxShadow: this.state.activeTab === "quality" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                    borderRadius: 4,
                                    border: "none",
                                }}
                            >
                                Data Quality
                            </Button>
                            <Button
                                type="text"
                                size="small"
                                onClick={() => this.setState({ activeTab: "profiling" })}
                                style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: this.state.activeTab === "profiling" ? "#0078d4" : "#666",
                                    background: this.state.activeTab === "profiling" ? "#fff" : "transparent",
                                    boxShadow: this.state.activeTab === "profiling" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                    borderRadius: 4,
                                    border: "none",
                                }}
                            >
                                Profiling Statistics
                            </Button>
                            <Button
                                type="text"
                                size="small"
                                onClick={() => this.setState({ activeTab: "ai" })}
                                style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: this.state.activeTab === "ai" ? "#0078d4" : "#666",
                                    background: this.state.activeTab === "ai" ? "#fff" : "transparent",
                                    boxShadow: this.state.activeTab === "ai" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                    borderRadius: 4,
                                    border: "none",
                                }}
                            >
                                AI Description
                            </Button>
                        </div>
                        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                            {this.state.activeTab === "profiling" && (
                                <>
                                    <div style={{ display: "flex", alignItems: "center", gap: 2, background: "#f5f5f5", borderRadius: 4, padding: 2 }}>
                                        <Button
                                            type="text"
                                            icon={<BarChartOutlined />}
                                            onClick={() => this.setState({ viewMode: "chart" })}
                                            size="small"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                color: this.state.viewMode === "chart" ? "#0078d4" : "#666",
                                                background: this.state.viewMode === "chart" ? "#fff" : "transparent",
                                                boxShadow: this.state.viewMode === "chart" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                                borderRadius: 4,
                                                border: "none",
                                            }}
                                        >
                                            Chart
                                        </Button>
                                        <Button
                                            type="text"
                                            icon={<TableOutlined />}
                                            onClick={() => this.setState({ viewMode: "table" })}
                                            size="small"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                color: this.state.viewMode === "table" ? "#0078d4" : "#666",
                                                background: this.state.viewMode === "table" ? "#fff" : "transparent",
                                                boxShadow: this.state.viewMode === "table" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                                borderRadius: 4,
                                                border: "none",
                                            }}
                                        >
                                            Table
                                        </Button>
                                    </div>
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<ExperimentOutlined />}
                                        loading={profilingJobActive}
                                        disabled={!this.props.dataSetId || profilingJobActive}
                                        onClick={this.handleRunProfiling}
                                        style={{ fontSize: 12, fontWeight: 500 }}
                                    >
                                        {profilingJobActive ? "Profiling..." : "Run Profiling"}
                                    </Button>
                                    <Button
                                        type="text"
                                        icon={<FullscreenOutlined style={{ color: "#666" }} />}
                                        onClick={() => this.setState({ profilingExpanded: true })}
                                        title="Fullscreen"
                                        size="small"
                                        style={{ padding: "4px 6px", borderRadius: 4, border: "none" }}
                                    />
                                </>
                            )}
                        </div>
                    </>
                }
                headerClassName="items-center gap-2"
            >
                <LoadingOverlay busy={this.isBusy()} spinner message={this.state.busyMessage || "Please wait..."}></LoadingOverlay>
                {!this.isBusy() ? (
                    this.state.permissionDenied ? (
                        <ProhibitedArea></ProhibitedArea>
                    ) : this.state.activeTab === "quality" ? (
                        <DataSetInsightsList dataSetInsightList={this.props.dataSetId ? this.props.dataSetFieldInsightsResult.Items : []} />
                    ) : this.state.activeTab === "ai" ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 24 }}>
                            <AIButton
                                onClick={this.handleOpenAiSummary}
                                disabled={!canGenerateDescription}
                                loading={this.props.datasetDescriptionLoading}
                                tooltipTitle="Generate an AI summary and data-quality assessment for this dataset"
                                disabledTooltipTitle={!profilingAvailable ? "Run profiling first to enable the AI summary." : "Dataset context is unavailable."}
                                buttonText="AI Summary"
                            />
                        </div>
                    ) : (
                        <ProfilingStatistics profilingData={profilingData} viewMode={this.state.viewMode} />
                    )
                ) : (
                    <></>
                )}
                <Modal
                    open={this.state.profilingExpanded}
                    onCancel={() => this.setState({ profilingExpanded: false })}
                    footer={null}
                    width="95vw"
                    style={{ top: 20 }}
                    styles={{ body: { height: "85vh", overflow: "auto", padding: "16px 24px" } }}
                    title={
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span>Profiling Statistics</span>
                            <div style={{ marginLeft: "auto", display: "flex", gap: 6, paddingRight: 24 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 2, background: "#f5f5f5", borderRadius: 4, padding: 2 }}>
                                    <Button
                                        type="text"
                                        icon={<BarChartOutlined />}
                                        onClick={() => this.setState({ viewMode: "chart" })}
                                        size="small"
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: this.state.viewMode === "chart" ? "#0078d4" : "#666",
                                            background: this.state.viewMode === "chart" ? "#fff" : "transparent",
                                            boxShadow: this.state.viewMode === "chart" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                            borderRadius: 4,
                                            border: "none",
                                        }}
                                    >
                                        Chart
                                    </Button>
                                    <Button
                                        type="text"
                                        icon={<TableOutlined />}
                                        onClick={() => this.setState({ viewMode: "table" })}
                                        size="small"
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: this.state.viewMode === "table" ? "#0078d4" : "#666",
                                            background: this.state.viewMode === "table" ? "#fff" : "transparent",
                                            boxShadow: this.state.viewMode === "table" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                            borderRadius: 4,
                                            border: "none",
                                        }}
                                    >
                                        Table
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                >
                    <ProfilingStatistics profilingData={profilingData} viewMode={this.state.viewMode} />
                </Modal>
                <DatasetDescriptionModal
                    open={this.state.aiSummaryModalOpen}
                    onClose={() => this.setState({ aiSummaryModalOpen: false })}
                    summary={aiSummaryForThisDataset}
                    loading={this.props.datasetDescriptionLoading}
                    error={this.props.getDatasetDescriptionResult && this.props.getDatasetDescriptionResult.success === false}
                    errorMessage={this.props.getDatasetDescriptionResult && this.props.getDatasetDescriptionResult.message}
                    onRegenerate={() => this.handleGenerateDescription(true)}
                />
            </FlexContainerLayout>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        dataSetFieldInsightsResult: state.dataSets.dataSetFieldInsightsResult,
        getDataSetFieldInsightsResult: state.dataSets.getDataSetFieldInsightsResult,
        jobsResult: state.dataSets.jobsResult,
        runDataSetProfilingResult: state.dataSets.runDataSetProfilingResult,
        profilingJobRunning: state.dataSets.profilingJobRunning,
        dataSet: state.dataSets.dataSet,
        datasetDescription: state.dataObjects.datasetDescription,
        datasetDescriptionLoading: state.dataObjects.datasetDescriptionLoading,
        getDatasetDescriptionResult: state.dataObjects.getDatasetDescriptionResult,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getDataSetFieldInsights: (dataSetId) => dispatch(actions.getDataSetFieldInsightsRequest(dataSetId)),
        getDataSetJobs: (dataSetId) => dispatch(actions.getDataSetJobsRequest(dataSetId)),
        runDataSetProfiling: (dataSetId) => dispatch(actions.runDataSetProfilingRequest(dataSetId)),
        getDatasetDescription: (businessAreaId, schemaId, dataSetId, regenerate) =>
            dispatch(actions.getDatasetDescriptionRequest(businessAreaId, schemaId, dataSetId, regenerate)),
        resetDatasetDescription: () => dispatch(actions.resetDatasetDescription()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DataSetInsightsHomeOverview));
